import { z } from 'zod';
import {
  FindingSeveritySchema,
  SignalCategorySchema,
  TargetTypeSchema,
} from '@terra/shared-schemas-orchestrator';

export const FINDING_ATTACK_PATH_RELATIONS = [
  'REACHES',
  'EXPOSES',
  'RESOLVES_TO',
  'WITHIN_NETBLOCK',
  'IS_SUBDOMAIN_OF',
  'FRONTED_BY',
  'RUNS_SOFTWARE',
  'SERVES',
  'HAS_WEAKNESS',
  'AUTHENTICATES',
  'EXPLOITED_AS',
  'CHAINS_INTO',
  'DELEGATES_TO',
  'CALLS_TOOL',
  'CALLS_MCP_TOOL',
  'CALLS_MCP_PROMPT',
  'CALLS_MCP_RESOURCE',
  'CALLS_MCP_PROTOCOL',
  'QUERIES_RAG',
  'USES_MODEL',
  'READS_MEMORY',
  'WRITES_MEMORY',
  'EXPOSES_INTERFACE',
  'REQUIRES_APPROVAL',
  'CROSSES_TRUST_BOUNDARY',
] as const;

export const FINDING_ATTACK_PATH_EDGE_VARIANTS = [
  'proven',
  'inferred',
] as const;

export const FindingAttackPathAssetKindSchema = z.enum([
  'APPLICATION',
  'AI_APPLICATION',
  'SURFACE',
  'ENDPOINT',
  'WEBPAGE',
  'AGENT',
  'SUB_AGENT',
  'TOOL',
  'MCP_SERVER',
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
  'MCP_PROTOCOL',
  'RAG',
  'MODEL',
  'MEMORY',
  'GUARDRAIL',
  'INTEGRATION',
  'NETBLOCK',
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
]);

export const FindingAttackPathRelationSchema = z.enum(
  FINDING_ATTACK_PATH_RELATIONS,
);

export const FindingAttackPathEdgeVariantSchema = z.enum(
  FINDING_ATTACK_PATH_EDGE_VARIANTS,
);

export const FindingAttackPathEntryNodeSchema = z.object({
  id: z.string(),
  kind: z.literal('ENTRY'),
  entryType: z.enum(['INTERNET', 'CONNECTOR']),
  provider: z.enum(['twingate', 'tailscale']).nullable(),
  networkName: z.string().nullable(),
  label: z.string(),
});

export const FindingAttackPathAssetNodeSchema = z.object({
  id: z.string(),
  kind: z.literal('ASSET'),
  assetKind: FindingAttackPathAssetKindSchema,
  label: z.string(),
  sublabel: z.string().nullable(),
  assetId: z.string().nullable(),
});

export const FindingAttackPathWeaknessNodeSchema = z.object({
  id: z.string(),
  kind: z.literal('WEAKNESS'),
  signalId: z.string(),
  title: z.string(),
  category: SignalCategorySchema.nullable(),
  isAuthenticated: z.boolean(),
});

export const FindingAttackPathCredentialNodeSchema = z.object({
  id: z.string(),
  kind: z.literal('CREDENTIAL'),
  credentialId: z.string().nullable(),
  username: z.string().nullable(),
  roleName: z.string().nullable(),
  label: z.string(),
});

export const FindingAttackPathFindingNodeSchema = z.object({
  id: z.string(),
  kind: z.literal('FINDING'),
  findingId: z.string(),
  title: z.string(),
  severity: FindingSeveritySchema,
  isCurrent: z.boolean(),
});

export const FindingAttackPathNodeSchema = z.discriminatedUnion('kind', [
  FindingAttackPathEntryNodeSchema,
  FindingAttackPathAssetNodeSchema,
  FindingAttackPathWeaknessNodeSchema,
  FindingAttackPathCredentialNodeSchema,
  FindingAttackPathFindingNodeSchema,
]);

export const FindingAttackPathEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  relation: FindingAttackPathRelationSchema,
  variant: FindingAttackPathEdgeVariantSchema,
});

export const FindingAttackPathDtoSchema = z.object({
  findingId: z.string(),
  targetType: TargetTypeSchema,
  nodes: z.array(FindingAttackPathNodeSchema),
  edges: z.array(FindingAttackPathEdgeSchema),
  truncated: z.boolean(),
});

export type FindingAttackPathAssetKind = z.infer<
  typeof FindingAttackPathAssetKindSchema
>;
export type FindingAttackPathRelation = z.infer<
  typeof FindingAttackPathRelationSchema
>;
export type FindingAttackPathEdgeVariant = z.infer<
  typeof FindingAttackPathEdgeVariantSchema
>;
export type FindingAttackPathNode = z.infer<typeof FindingAttackPathNodeSchema>;
export type FindingAttackPathEdge = z.infer<typeof FindingAttackPathEdgeSchema>;
export type FindingAttackPathDto = z.infer<typeof FindingAttackPathDtoSchema>;
